import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './NotificationPanel.css';

/**
 * Notification dropdown panel.
 *
 * Displays a scrollable list of notifications when the bell icon is clicked.
 * Shows an empty state when there are no notifications.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the panel is visible
 * @param {function} props.onClose - Handler to close the panel
 * @param {Array} props.notifications - Array of notification objects
 * @returns {JSX.Element|null}
 */
function NotificationPanel({
  isOpen,
  notifications,
  isLoading = false,
  errorMessage = '',
  onClearAll,
  onNotificationClick,
}) {
  if (!isOpen) return null;

  return (
    <div className="notification-panel" role="dialog" aria-label="Notifications">
      {/* Header */}
      <div className="notification-panel__header">
        <h3 className="notification-panel__title">Notifications</h3>
        <button
          type="button"
          className="notification-panel__clear"
          onClick={onClearAll}
          disabled={notifications.length === 0 || isLoading}
        >
          Clear all
        </button>
      </div>

      {/* Content */}
      <div className="notification-panel__content">
        {isLoading ? (
          <div className="notification-panel__empty">
            <span className="notification-panel__empty-icon" aria-hidden="true">&#128276;</span>
            <p className="notification-panel__empty-title">Loading notifications...</p>
            <span className="notification-panel__empty-desc">
              Checking your latest activity.
            </span>
          </div>
        ) : errorMessage ? (
          <div className="notification-panel__empty">
            <span className="notification-panel__empty-icon" aria-hidden="true">&#9888;</span>
            <p className="notification-panel__empty-title">{errorMessage}</p>
            <span className="notification-panel__empty-desc">
              Try opening notifications again in a moment.
            </span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-panel__empty">
            <span className="notification-panel__empty-icon" aria-hidden="true">&#128276;</span>
            <p className="notification-panel__empty-title">No notifications at this time</p>
            <span className="notification-panel__empty-desc">
              When you receive a notification, it will show up here.
            </span>
          </div>
        ) : (
          <div className="notification-panel__list">
            {notifications.map((notif) => (
              <NotificationItem
                key={notif.id}
                notification={notif}
                onClick={() => onNotificationClick?.(notif)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual notification item.
 */
function NotificationItem({ notification, onClick }) {
  const {
    title,
    body,
    createdAt,
    isRead,
    actorUsername,
    actorProfileImage,
  } = notification;

  const formatTimestamp = (value) => {
    if (!value) {
      return '';
    }

    const created = new Date(value);
    if (Number.isNaN(created.getTime())) {
      return '';
    }

    const diffMs = Date.now() - created.getTime();
    const diffMinutes = Math.max(Math.floor(diffMs / 60000), 0);
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return created.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <button
      type="button"
      className={`notification-item ${!isRead ? 'notification-item--unread' : ''}`}
      onClick={onClick}
    >
      <NotificationAvatar
        username={actorUsername}
        profileImage={actorProfileImage}
      />
      <div className="notification-item__content">
        <p className="notification-item__title">{title}</p>
        <p className="notification-item__text">{body}</p>
        <span className="notification-item__time">{formatTimestamp(createdAt)}</span>
      </div>
      {!isRead && <span className="notification-item__dot" />}
    </button>
  );
}

function NotificationAvatar({ username, profileImage }) {
  const [imageFailed, setImageFailed] = useState(false);
  const fallbackText = username?.trim()?.charAt(0)?.toUpperCase() || 'PA';

  return (
    <div className="notification-item__avatar" aria-hidden="true">
      {profileImage && !imageFailed ? (
        <img
          src={profileImage}
          alt=""
          className="notification-item__avatar-image"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="notification-item__avatar-fallback">{fallbackText}</span>
      )}
    </div>
  );
}

NotificationPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  notifications: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClearAll: PropTypes.func,
  onNotificationClick: PropTypes.func,
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    actorUsername: PropTypes.string,
    actorProfileImage: PropTypes.string,
    actionUrl: PropTypes.string,
    createdAt: PropTypes.string,
    isRead: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func,
};

NotificationAvatar.propTypes = {
  username: PropTypes.string,
  profileImage: PropTypes.string,
};

export default NotificationPanel;
